import type { Point } from "@/document/operations/pathGeometry";
import type { Transform } from "@/document/model/nodes";
import { createIdentityTransform } from "@/document/model/nodes";
import { readSvgNumberList } from "./parseSvgDocument";

export interface SvgTransformMatrix {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

export const IDENTITY_SVG_MATRIX: SvgTransformMatrix = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

export function parseSvgTransformAttribute(value: string | null | undefined): SvgTransformMatrix {
  if (!value?.trim()) {
    return IDENTITY_SVG_MATRIX;
  }

  let matrix = IDENTITY_SVG_MATRIX;
  const transformRegex = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]*)\)/gi;
  let match: RegExpExecArray | null;
  while ((match = transformRegex.exec(value)) !== null) {
    const kind = match[1]!.toLowerCase();
    const args = readSvgNumberList(match[2]);
    matrix = multiplySvgMatrices(matrix, transformMatrixForCommand(kind, args));
  }
  return matrix;
}

export function multiplySvgMatrices(left: SvgTransformMatrix, right: SvgTransformMatrix): SvgTransformMatrix {
  return {
    a: left.a * right.a + left.c * right.b,
    b: left.b * right.a + left.d * right.b,
    c: left.a * right.c + left.c * right.d,
    d: left.b * right.c + left.d * right.d,
    e: left.a * right.e + left.c * right.f + left.e,
    f: left.b * right.e + left.d * right.f + left.f,
  };
}

export function applySvgMatrix(point: Point, matrix: SvgTransformMatrix): Point {
  return {
    x: matrix.a * point.x + matrix.c * point.y + matrix.e,
    y: matrix.b * point.x + matrix.d * point.y + matrix.f,
  };
}

export function svgMatrixToTransform(matrix: SvgTransformMatrix): Transform {
  const rotation = Math.atan2(matrix.b, matrix.a) * (180 / Math.PI);
  const scaleX = Math.hypot(matrix.a, matrix.b);
  const determinant = matrix.a * matrix.d - matrix.b * matrix.c;
  const scaleY = determinant / scaleX || 1;
  return {
    ...createIdentityTransform(),
    x: matrix.e,
    y: matrix.f,
    rotation,
    scaleX: Math.abs(scaleX),
    scaleY: Math.abs(scaleY),
  };
}

function transformMatrixForCommand(kind: string, args: number[]): SvgTransformMatrix {
  switch (kind) {
    case "matrix":
      return {
        a: args[0] ?? 1,
        b: args[1] ?? 0,
        c: args[2] ?? 0,
        d: args[3] ?? 1,
        e: args[4] ?? 0,
        f: args[5] ?? 0,
      };
    case "translate":
      return { a: 1, b: 0, c: 0, d: 1, e: args[0] ?? 0, f: args[1] ?? 0 };
    case "scale": {
      const sx = args[0] ?? 1;
      const sy = args.length > 1 ? args[1]! : sx;
      return { a: sx, b: 0, c: 0, d: sy, e: 0, f: 0 };
    }
    case "rotate": {
      const angle = ((args[0] ?? 0) * Math.PI) / 180;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      if (args.length >= 3) {
        const cx = args[1]!;
        const cy = args[2]!;
        return multiplySvgMatrices(
          { a: 1, b: 0, c: 0, d: 1, e: cx, f: cy },
          multiplySvgMatrices(
            { a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 },
            { a: 1, b: 0, c: 0, d: 1, e: -cx, f: -cy },
          ),
        );
      }
      return { a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 };
    }
    case "skewx": {
      const angle = ((args[0] ?? 0) * Math.PI) / 180;
      return { a: 1, b: 0, c: Math.tan(angle), d: 1, e: 0, f: 0 };
    }
    case "skewy": {
      const angle = ((args[0] ?? 0) * Math.PI) / 180;
      return { a: 1, b: Math.tan(angle), c: 0, d: 1, e: 0, f: 0 };
    }
    default:
      return IDENTITY_SVG_MATRIX;
  }
}
